import { Menu, X } from 'lucide-react'
import { type ComponentProps, type ReactNode, useEffect } from 'react'
import { cn } from '../lib/cn'
import { Avatar } from './avatar'
import { Button } from './button'
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from './dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from './dropdown-menu'
export function AppShell({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      data-slot="app-shell"
      className={cn(
        'min-h-dvh bg-background lg:grid lg:grid-cols-[280px_minmax(0,1fr)]',
        className,
      )}
    />
  )
}
export function AppShellSidebar({ className, ...props }: ComponentProps<'aside'>) {
  return (
    <aside
      {...props}
      data-slot="app-shell-sidebar"
      className={cn(
        'sticky top-0 hidden h-dvh flex-col overflow-y-auto border-r border-border bg-card p-6 lg:flex',
        className,
      )}
    />
  )
}
export function AppShellHeader({ className, ...props }: ComponentProps<'header'>) {
  return (
    <header
      {...props}
      data-slot="app-shell-header"
      className={cn(
        'flex items-center justify-between gap-3 border-b border-border bg-card px-5 py-3 lg:hidden',
        className,
      )}
    />
  )
}
export function AppShellMain({ className, ...props }: ComponentProps<'main'>) {
  return (
    <main
      {...props}
      tabIndex={props.tabIndex ?? -1}
      data-slot="app-shell-main"
      className={cn('min-w-0 px-5 py-6 outline-none sm:px-8 lg:p-10', className)}
    />
  )
}
export function AppShellSkipLink({ className, ...props }: ComponentProps<'a'>) {
  return (
    <a
      {...props}
      className={cn(
        'sr-only fixed top-3 left-3 z-[100] rounded-control bg-card p-3 focus:not-sr-only focus:outline-2 focus:outline-ring',
        className,
      )}
    />
  )
}
export function AppShellPanel({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      data-slot="app-shell-panel"
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-[28px] border border-border bg-card text-foreground shadow-surface',
        className,
      )}
    />
  )
}
export function AppShellBrand({
  brand,
  subtitle,
  action,
  className,
  ...props
}: Omit<ComponentProps<'div'>, 'children'> & {
  brand: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
}) {
  return (
    <div
      {...props}
      className={cn('flex shrink-0 items-start justify-between gap-4 px-6 py-4', className)}
    >
      <div className="min-w-0">
        <div className="text-2xl leading-tight">{brand}</div>
        {subtitle ? (
          <p className="mt-1 truncate text-[10px] text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {action}
    </div>
  )
}
export function AppShellPanelBody({ className, ...props }: ComponentProps<'div'>) {
  return <div {...props} className={cn('min-h-0 flex-1 overflow-y-auto px-3 py-2', className)} />
}
export function AppShellUserMenu({
  name,
  caption,
  avatarFallback,
  children,
  ...props
}: ComponentProps<typeof DropdownMenu> & {
  name: ReactNode
  caption?: ReactNode
  avatarFallback: ReactNode
}) {
  return (
    <DropdownMenu {...props}>
      <DropdownMenuTrigger asChild>
        <Button
          appearance="ghost"
          tone="neutral"
          className="h-auto w-full justify-start rounded-none px-5 py-3 text-left"
        >
          <Avatar
            alt=""
            fallback={avatarFallback}
            size="sm"
            className="bg-action text-action-foreground ring-4 ring-accent [&>div]:text-action-foreground"
          />
          <span className="min-w-0">
            <span className="block truncate text-xs font-medium text-foreground">{name}</span>
            {caption ? (
              <span className="mt-0.5 block text-[9px] uppercase tracking-[0.16em] text-action">
                {caption}
              </span>
            ) : null}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
export function AppShellDrawer({
  open,
  onOpenChange,
  ...props
}: ComponentProps<typeof Dialog> & { open: boolean; onOpenChange: (open: boolean) => void }) {
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)')
    const closeOnDesktop = () => {
      if (desktop.matches) onOpenChange(false)
    }
    desktop.addEventListener('change', closeOnDesktop)
    return () => desktop.removeEventListener('change', closeOnDesktop)
  }, [onOpenChange])

  return <Dialog {...props} open={open} onOpenChange={onOpenChange} />
}
export function AppShellDrawerContent({ title, children }: { title: string; children: ReactNode }) {
  return (
    <DialogContent
      showCloseButton={false}
      aria-describedby={undefined}
      overlayClassName="bg-foreground/20 backdrop-blur-[2px] lg:hidden"
      placement="start"
      className="border-0 bg-transparent p-3 lg:hidden"
    >
      <DialogTitle className="sr-only">{title}</DialogTitle>
      {children}
    </DialogContent>
  )
}
export function AppShellDrawerTrigger({ label }: { label: string }) {
  return (
    <div className="flex h-16 shrink-0 items-center px-5 sm:h-[76px] sm:px-8 lg:hidden">
      <DialogTrigger asChild>
        <Button appearance="ghost" tone="neutral" size="icon" aria-label={label} className="mr-1">
          <Menu className="size-[18px]" strokeWidth={1.8} />
        </Button>
      </DialogTrigger>
    </div>
  )
}
export function AppShellDrawerClose({ label }: { label: string }) {
  return (
    <DialogClose asChild>
      <Button appearance="ghost" tone="neutral" size="icon" aria-label={label}>
        <X className="size-4" strokeWidth={1.8} />
      </Button>
    </DialogClose>
  )
}
